import { request } from '@/lib/apiClient';

const TOKEN_KEY = 'vms.cart.token';

/**
 * The guest cart handle.
 *
 * Kept in localStorage rather than a cookie so it is never sent automatically:
 * this token is the only thing standing between a guest's basket and anyone
 * who has it, and a cookie would attach it to every request including ones
 * that have no business seeing it.
 *
 * Every read and write is guarded. Private-browsing modes and blocked site
 * data both make localStorage throw rather than return null, and a shop that
 * cannot render because storage is disabled is worse than one without a
 * remembered basket.
 */
export const cartToken = {
  get() {
    try {
      return window.localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set(token) {
    if (!token) return;
    try {
      window.localStorage.setItem(TOKEN_KEY, token);
    } catch {
      /* storage unavailable - the cart still works for this page load */
    }
  },
  clear() {
    try {
      window.localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* nothing to do */
    }
  },
};

function withToken(config) {
  const token = cartToken.get();
  return token ? { ...config, headers: { ...config.headers, 'X-Cart-Token': token } } : config;
}

/**
 * Every response carries the whole cart, and every call funnels through here
 * so a freshly-issued guest token is stored the moment the server mints one.
 * Miss that and the next request starts a second, empty cart.
 */
async function cartRequest(config) {
  const cart = await request(withToken(config));
  if (cart?.token) cartToken.set(cart.token);
  return cart;
}

export const cartApi = {
  get: () => cartRequest({ url: '/storefront/cart' }),

  addItem: (offerId, quantity = 1) =>
    cartRequest({
      url: '/storefront/cart/items',
      method: 'POST',
      data: { offer_id: offerId, quantity },
    }),

  setQuantity: (itemId, quantity) =>
    cartRequest({
      url: `/storefront/cart/items/${itemId}`,
      method: 'PATCH',
      data: { quantity },
    }),

  removeItem: (itemId) =>
    cartRequest({ url: `/storefront/cart/items/${itemId}`, method: 'DELETE' }),

  clear: () => request(withToken({ url: '/storefront/cart', method: 'DELETE' })),

  /** The header the checkout call needs, so the order is built from this cart. */
  header: () => {
    const token = cartToken.get();
    return token ? { 'X-Cart-Token': token } : {};
  },
};
